import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from "react";
import { BrowserRouter, Routes, Route, useParams, Navigate } from "react-router-dom";
import { LIGHT, DARK, applyAgendaBranding } from "./theme";
import { api } from "./api";
import { Toast } from "./ui";
import { I } from "./Icons";
import LoginPage from "./pages/LoginPage";
import AgendaPage from "./pages/AgendaPage";
import MasterDashboard from "./pages/MasterDashboard";

// ── Auth Context ──────────────────────────────────────────────────────────────
const AuthCtx = createContext(null);
export function useAuth() { return useContext(AuthCtx); }

// ── Notification log (in-memory, compartilhado entre instancias) ──────────────
let _log = [], _subs = [];
export function pushNotif(msg) {
  const n = { id: Date.now(), msg, time: new Date().toLocaleTimeString("pt-BR", { hour: "2-digit", minute: "2-digit" }) };
  _log = [n, ..._log].slice(0, 40);
  _subs.forEach(f => f([..._log]));
  if ("Notification" in window && Notification.permission === "granted") {
    new Notification("Agenda de Shows", { body: msg });
  }
}
export function useNotifLog() {
  const [l, setL] = useState([..._log]);
  useEffect(() => {
    _subs.push(setL);
    return () => { _subs = _subs.filter(f => f !== setL); };
  }, []);
  return l;
}

// ── SSE: conexao persistente com o servidor para receber notificacoes ─────────
export function useSSE(token, notifOn) {
  const ctrlRef = useRef(null);

  useEffect(() => {
    if (!token || !notifOn) {
      if (ctrlRef.current) { ctrlRef.current.abort(); ctrlRef.current = null; }
      return;
    }

    const base = process.env.REACT_APP_API_URL || window.location.origin;
    const url = base + "/api/notify/subscribe";
    let retryTimer = null;

    function connect() {
      if (ctrlRef.current) ctrlRef.current.abort();
      const ctrl = new AbortController();
      ctrlRef.current = ctrl;

      fetch(url, { headers: { Authorization: "Bearer " + token }, signal: ctrl.signal })
        .then(res => {
          if (!res.ok) throw new Error("SSE error");
          const reader = res.body.getReader();
          const decoder = new TextDecoder();
          let buffer = "";

          function read() {
            reader.read().then(({ done, value }) => {
              if (done) { retryTimer = setTimeout(connect, 3000); return; }
              buffer += decoder.decode(value, { stream: true });
              const lines = buffer.split("\n");
              buffer = lines.pop();
              lines.forEach(line => {
                if (line.startsWith("data: ")) {
                  try { pushNotif(JSON.parse(line.slice(6)).msg); } catch {}
                }
              });
              read();
            }).catch(() => { retryTimer = setTimeout(connect, 3000); });
          }
          read();
        })
        .catch(err => {
          if (err.name !== "AbortError") retryTimer = setTimeout(connect, 5000);
        });
    }

    connect();
    return () => {
      if (ctrlRef.current) ctrlRef.current.abort();
      if (retryTimer) clearTimeout(retryTimer);
    };
  }, [token, notifOn]);
}

// ── Manifest dinamico por slug ────────────────────────────────────────────────
function setDynamicManifest(slug) {
  if (!slug || slug === "admin") return;
  document.querySelectorAll("link[rel='manifest']").forEach(el => el.remove());
  const link = document.createElement("link");
  link.rel = "manifest";
  link.href = "manifest.json";
  document.head.appendChild(link);
}

// ── App Shell ─────────────────────────────────────────────────────────────────
export default function App() {
  const [token,   setToken]   = useState(() => localStorage.getItem("token"));
  const [user,    setUser]    = useState(null);
  const [agenda,  setAgenda]  = useState(null);
  const [dark,    setDark]    = useState(() => localStorage.getItem("dark") === "1");
  const [toast,   setToast]   = useState(null);
  const [loading, setLoading] = useState(!!localStorage.getItem("token"));
  // notifOn: true por padrao, persiste no localStorage
  const [notifOn, setNotifOn] = useState(() => localStorage.getItem("notifOn") !== "0");
  const T = dark ? DARK : LIGHT;

  // O deploy gratuito é serverless; notificações em tempo real não mantêm
  // conexões persistentes. A agenda é atualizada pelo cron horário.

  function toast_(msg, ms = 3000) { setToast(msg); setTimeout(() => setToast(null), ms); }
  function toggleDark() { setDark(d => { localStorage.setItem("dark", d ? "0" : "1"); return !d; }); }

  // Pede permissao de notificacao automaticamente ao abrir o site
  useEffect(() => {
    if (!("Notification" in window)) return;
    if (Notification.permission === "default") {
      Notification.requestPermission();
    }
  }, []);

  async function toggleNotif() {
    if (notifOn) {
      setNotifOn(false);
      localStorage.setItem("notifOn", "0");
      toast_("Notificacoes desativadas");
    } else {
      if ("Notification" in window && Notification.permission !== "granted") {
        const p = await Notification.requestPermission();
        if (p === "denied") { toast_("Permissao negada nas configuracoes"); return; }
      }
      setNotifOn(true);
      localStorage.setItem("notifOn", "1");
      toast_("Notificacoes ativadas");
    }
  }

  const login = useCallback(async (username, password, slug) => {
    const data = await api.login(username, password, slug);
    localStorage.setItem("token", data.token);
    setToken(data.token);
    setUser(data.user);
    setAgenda(data.agenda);
    if (data.agenda) applyAgendaBranding(data.agenda);
  }, []);

  const logout = useCallback(() => {
    localStorage.removeItem("token");
    setToken(null); setUser(null); setAgenda(null);
  }, []);

  // Restaura sessao ao carregar
  useEffect(() => {
    if (!token) { setLoading(false); return; }
    api.me(token)
      .then(data => {
        setUser(data.user);
        setAgenda(data.agenda);
        if (data.agenda) applyAgendaBranding(data.agenda);
      })
      .catch(() => { localStorage.removeItem("token"); setToken(null); })
      .finally(() => setLoading(false));
  }, [token]);

  if (loading) return (
    <div style={{ minHeight: "100vh", background: T.bg, display: "flex", alignItems: "center", justifyContent: "center" }}>
      <style>{`@keyframes spin{to{transform:rotate(360deg)}}`}</style>
      <div style={{ width: 36, height: 36, border: `3px solid ${T.border}`, borderTop: `3px solid ${T.accent}`, borderRadius: "50%", animation: "spin 0.8s linear infinite" }} />
    </div>
  );

  return (
    <AuthCtx.Provider value={{ token, user, agenda, login, logout, setAgenda, toast: toast_, T, dark, toggleDark, notifOn, toggleNotif }}>
      <style>{`
        *{box-sizing:border-box;margin:0;padding:0;}
        body{background:${T.bg};}
        ::-webkit-scrollbar{width:5px;height:5px}
        ::-webkit-scrollbar-thumb{background:${T.border};border-radius:4px}
        @keyframes slideUp{from{transform:translateY(100%);opacity:0}to{transform:translateY(0);opacity:1}}
        @keyframes fadeIn{from{opacity:0}to{opacity:1}}
        @keyframes shake{0%,100%{transform:translateX(0)}25%{transform:translateX(-7px)}75%{transform:translateX(7px)}}
        @keyframes spin{to{transform:rotate(360deg)}}
      `}</style>
      <Toast msg={toast} T={T} />
      <BrowserRouter>
        <Routes>
          <Route path="/"            element={<Navigate to="/admin" replace />} />
          <Route path="/admin"       element={<MasterRoute><MasterDashboard /></MasterRoute>} />
          <Route path="/admin/login" element={<LoginPage slug={null} />} />
          <Route path="/:slug/login" element={<SlugLogin />} />
          <Route path="/:slug"       element={<AgendaRoute><AgendaPage /></AgendaRoute>} />
          <Route path="*"            element={<NotFound T={T} />} />
        </Routes>
      </BrowserRouter>
    </AuthCtx.Provider>
  );
}

// ── Route guards ──────────────────────────────────────────────────────────────
function MasterRoute({ children }) {
  const { user, loading } = useAuth();
  // Nao redireciona enquanto ainda esta carregando sessao
  if (!user) return <Navigate to="/admin/login" replace />;
  if (user.role !== "master") return <Navigate to="/admin/login" replace />;
  return children;
}

function SlugLogin() {
  const { slug } = useParams();
  useEffect(() => { setDynamicManifest(slug); }, [slug]);
  return <LoginPage slug={slug} />;
}

function AgendaRoute({ children }) {
  const { user, agenda } = useAuth();
  const { slug } = useParams();
  useEffect(() => { setDynamicManifest(slug); }, [slug]);
  // Nao tem sessao: vai para login
  if (!user || !agenda) return <Navigate to={"/" + slug + "/login"} replace />;
  // Sessao de agenda diferente: redireciona para a agenda correta
  if (agenda.slug !== slug) return <Navigate to={"/" + agenda.slug} replace />;
  return children;
}

function NotFound({ T }) {
  return (
    <div style={{ minHeight: "100vh", background: T.bg, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", color: T.textMuted, gap: 12 }}>
      <I n="Music" size={48} style={{ color: T.border }} />
      <div style={{ fontSize: 18, fontWeight: 700, color: T.text }}>Agenda nao encontrada</div>
      <div style={{ fontSize: 13 }}>Verifique o link e tente novamente.</div>
    </div>
  );
}
